import {
  BedIcon,
  CalendarDotsIcon,
  CopyIcon,
  MapPinIcon,
} from "@phosphor-icons/react";
import copy from "copy-to-clipboard";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button";
import { Divider } from "@/components/Divider/Divider";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { IconButton } from "@/components/IconButton/IconButton";
import { Image } from "@/components/Image/Image";
import { HotelRoomTitle } from "@/features/hotels/components/hotel/HotelRoomTitle";
import { HotelName } from "@/features/hotels/components/hotels/HotelName";
import { RoomDetails } from "@/features/hotels/components/RoomSelection/RoomDetails";
import type {
  HotelBooking,
  HotelBookingRoomOption,
  HotelData,
} from "@/features/hotels/types/types";
import { getNumberOfBookingNights } from "@/features/hotels/utils/getNumberOfBookingNights";

const DetailsRow = ({
  icon: Icon,
  label,
  secondaryLabel,
  hasCopyButton,
}: {
  icon: React.ElementType;
  label: React.ReactNode;
  secondaryLabel: React.ReactNode;
  hasCopyButton?: boolean;
}) => {
  const secondaryLabelRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex gap-2 text-text-primary">
      <Icon className="size-5 shrink-0" />

      <div className="grow-1">
        <div className="mb-1 font-medium">{label}</div>
        <div className="flex justify-between text-sm text-text-tertiary">
          <div ref={secondaryLabelRef}>{secondaryLabel}</div>

          {hasCopyButton ? (
            <div className="shrink-0 -translate-y-1.5">
              <IconButton
                size="small"
                onClick={() => {
                  copy(secondaryLabelRef.current?.textContent || "");
                }}
              >
                <CopyIcon size={16} />
              </IconButton>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

type Props = {
  hotel: HotelData;
  hotelBookingData: HotelBooking;
  rooms?: { adults: number; children: number }[];
  checkIn: Date;
  checkOut: Date;
};

export const HotelAndRoomCard = ({
  hotel,
  hotelBookingData,
  rooms,
  checkIn,
  checkOut,
}: Props) => {
  const intl = useIntl();

  const bookingNights = getNumberOfBookingNights({
    checkIn,
    checkOut,
  });

  const [selectedRoomDetails, setSelectedRoomDetails] =
    useState<HotelBookingRoomOption | null>(null);

  return (
    <>
      <div className="section-card flex flex-col gap-4 pb-2">
        <div className="flex justify-between gap-4">
          <div className="text-lg font-semibold text-text-title">
            <HotelName name={hotel.name} />
          </div>

          <Image
            className="h-17.5 w-15 shrink-0 rounded-lg object-cover"
            src={hotel.mainImageUrl || "/imagePlaceholder.svg"}
            alt={hotel.name}
          />
        </div>

        <Divider color="neutral-soft" />

        <DetailsRow
          icon={CalendarDotsIcon}
          label={
            <>
              {intl.formatDate(checkIn, {
                month: "short",
                day: "numeric",
                weekday: "short",
              })}{" "}
              -{" "}
              {intl.formatDate(checkOut, {
                month: "short",
                day: "numeric",
                weekday: "short",
                year: "numeric",
              })}
            </>
          }
          secondaryLabel={intl.formatMessage(
            {
              defaultMessage: "{count, plural, one {# night} other {# nights}}",
              description:
                "Night count used in booking summaries (e.g. '3 nights')",
            },
            { count: bookingNights },
          )}
        />

        <DetailsRow
          icon={MapPinIcon}
          label={intl.formatMessage({
            defaultMessage: "Location",
            description: "Label for the location of the hotel",
          })}
          secondaryLabel={hotel.address}
          hasCopyButton
        />

        <DetailsRow
          icon={BedIcon}
          label={intl.formatMessage({
            defaultMessage: "Selected room",
            description: "Label for the selected room in the hotel",
          })}
          secondaryLabel={
            <>
              {rooms &&
                hotelBookingData.rooms.map((room, index) => (
                  <HotelRoomTitle
                    key={room.roomToken}
                    name={room.name}
                    board={room.boardBasis}
                    occupancy={{
                      numberOfAdults: rooms[index].adults,
                      numberOfChildren: rooms[index].children,
                    }}
                  />
                ))}
            </>
          }
        />

        <Button
          type="button"
          variant="text"
          size="small"
          fullWidth
          onClick={() => {
            setSelectedRoomDetails(hotelBookingData.rooms[0]);
          }}
        >
          {intl.formatMessage({
            defaultMessage: "Room details",
            description:
              "Section heading for room details within a hotel booking",
          })}
        </Button>
      </div>

      <SwipeableDrawer
        open={!!selectedRoomDetails}
        hasFullHeight
        heightOffset={24}
        onClose={() => setSelectedRoomDetails(null)}
        backgroundColor="var(--color-surface-background)"
        keepMounted={false}
        disableRestoreFocus
      >
        <DrawerTopBar
          onCloseClick={() => setSelectedRoomDetails(null)}
          title={intl.formatMessage({
            defaultMessage: "Room details",
            description: "Section heading for room details and description",
          })}
        />
        {selectedRoomDetails ? (
          <RoomDetails option={selectedRoomDetails} hotelName={hotel.name} />
        ) : null}
      </SwipeableDrawer>
    </>
  );
};
